import { cn } from "../../lib/utils";

export type NetworkStatusBarStatus = "online" | "offline" | "slow";

export interface NetworkStatusBarProps {
  /** Current connectivity status. */
  status: NetworkStatusBarStatus;
  /** Average latency from the last speed test (ms), or null. */
  latencyMs?: number | null;
  /** Whether a speed test is in progress. */
  testing?: boolean;
  /** Callback when the user clicks "Run speed test". */
  onRunTest?: () => void;
  /** Callback when the user clicks the close/dismiss button. */
  onDismiss?: () => void;
}

/**
 * A slim, full-width banner that displays connectivity state.
 * Renders nothing when status is `"online"` and no test results are shown.
 *
 * Pure presentational — no hooks, no store access.
 */
export function NetworkStatusBar({
  status,
  latencyMs,
  testing = false,
  onRunTest,
  onDismiss,
}: NetworkStatusBarProps) {
  if (status === "online" && latencyMs == null && !testing) return null;

  const isOffline = status === "offline";
  const isSlow = status === "slow";

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-center justify-between gap-3 px-4 py-1.5 text-sm font-medium transition-colors",
        isOffline && "bg-destructive/10 text-destructive",
        isSlow && "bg-amber-500/10 text-amber-700 dark:text-amber-400",
        status === "online" &&
          "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
      )}
    >
      <div className="flex items-center gap-2">
        {/* Pulsing dot */}
        <span
          className={cn(
            "relative flex size-2",
            isOffline && "text-destructive",
            isSlow && "text-amber-500",
            status === "online" && "text-emerald-500",
          )}
        >
          {(isOffline || isSlow) && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-75" />
          )}
          <span className="relative inline-flex size-2 rounded-full bg-current" />
        </span>

        <span>
          {isOffline && "You are offline — check your internet connection"}
          {isSlow && "Slow connection detected"}
          {status === "online" && latencyMs != null && "Connection is healthy"}
        </span>

        {latencyMs != null && (
          <span className="text-xs opacity-70">({latencyMs} ms)</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {onRunTest && (
          <button
            type="button"
            disabled={testing}
            onClick={onRunTest}
            className={cn(
              "shrink-0 rounded-md px-2.5 py-0.5 text-xs font-semibold transition-colors cursor-pointer",
              "border border-current/20 hover:bg-current/10 disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {testing ? "Testing..." : "Run speed test"}
          </button>
        )}

        {/* Close button — only when not offline */}
        {!isOffline && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="shrink-0 rounded-md p-0.5 transition-colors cursor-pointer hover:bg-current/10"
            aria-label="Dismiss"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-3.5"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
